import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { CitationAssetSheetOrganization } from "../../../models/workPaperTemplate";
import { Info } from "lucide-react";

const DEFAULT_TEMPLATES: Record<CitationAssetSheetOrganization, string> = {
  by_sample: "P{procedure_index}_{sample}",
  by_eval_column: "{sample}_{column}",
  by_attribute: "{prefix}_{attribute}",
  by_attribute_and_sample: "{sample}_{attribute}",
};

const TEMPLATE_VARIABLES: Record<
  CitationAssetSheetOrganization,
  Array<{ name: string; description: string }>
> = {
  by_sample: [
    { name: "{prefix}", description: "The sheet name prefix value" },
    {
      name: "{procedure_index}",
      description: "1-based procedure index (1, 2, 3...)",
    },
    { name: "{procedure_name}", description: "Name of the procedure" },
    { name: "{sample}", description: "Sample display string (e.g., S1, S2)" },
  ],
  by_eval_column: [
    { name: "{prefix}", description: "The sheet name prefix value" },
    {
      name: "{procedure_index}",
      description: "1-based procedure index (1, 2, 3...)",
    },
    { name: "{procedure_name}", description: "Name of the procedure" },
    { name: "{sample}", description: "Sample display string (e.g., S1, S2)" },
    { name: "{column}", description: "Column display string" },
  ],
  by_attribute: [
    { name: "{prefix}", description: "The sheet name prefix value" },
    {
      name: "{procedure_index}",
      description: "1-based procedure index (1, 2, 3...)",
    },
    { name: "{procedure_name}", description: "Name of the procedure" },
    { name: "{attribute}", description: "Attribute display string" },
  ],
  by_attribute_and_sample: [
    { name: "{prefix}", description: "The sheet name prefix value" },
    {
      name: "{procedure_index}",
      description: "1-based procedure index (1, 2, 3...)",
    },
    { name: "{procedure_name}", description: "Name of the procedure" },
    { name: "{sample}", description: "Sample display string (e.g., S1, S2)" },
    { name: "{attribute}", description: "Attribute display string" },
  ],
};

export interface CitationAssetSheetFieldFormData {
  sheetNamePrefix: string;
  sheetNameTemplate?: string;
  organization?: CitationAssetSheetOrganization;
  startRow?: number;
  imagesPerRow?: number;
  includeAnnotations?: boolean;
  includeNonAttributeTests?: boolean;
  includeProcedureIds?: string[] | null;
  truncateEvalColumnStepsTaken?: boolean;
}

interface CitationAssetSheetFieldFormProps {
  value: CitationAssetSheetFieldFormData;
  onChange: (value: CitationAssetSheetFieldFormData) => void;
  readOnly?: boolean;
}

export function CitationAssetSheetFieldForm({
  value,
  onChange,
  readOnly = false,
}: CitationAssetSheetFieldFormProps) {
  const [formData, setFormData] =
    useState<CitationAssetSheetFieldFormData>(value);

  useEffect(() => {
    setFormData(value);
  }, [value]);

  const handleChange = (
    field: keyof CitationAssetSheetFieldFormData,
    newValue:
      | string
      | number
      | boolean
      | CitationAssetSheetOrganization
      | string[]
      | null
      | undefined,
  ) => {
    const updated = { ...formData, [field]: newValue };
    setFormData(updated);
    onChange(updated);
  };

  const currentOrganization = formData.organization ?? "by_sample";
  const defaultTemplate = DEFAULT_TEMPLATES[currentOrganization];
  const availableVariables = TEMPLATE_VARIABLES[currentOrganization];
  const effectiveTemplate = formData.sheetNameTemplate || defaultTemplate;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="sheetNamePrefix">Sheet Name Prefix</Label>
        <Input
          id="sheetNamePrefix"
          value={formData.sheetNamePrefix}
          onChange={(e) => handleChange("sheetNamePrefix", e.target.value)}
          placeholder="Citations"
          disabled={readOnly}
        />
        <p className="text-sm text-muted-foreground">
          Prefix for generated sheet names (e.g., &quot;Citations&quot; creates
          &quot;Citations_ProcedureName&quot;)
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="organization">Organization</Label>
        <Select
          value={currentOrganization}
          onValueChange={(value) =>
            handleChange(
              "organization",
              value as CitationAssetSheetOrganization,
            )
          }
          disabled={readOnly}
        >
          <SelectTrigger id="organization">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="by_sample">By Sample</SelectItem>
            <SelectItem value="by_eval_column">By Evaluation Column</SelectItem>
            <SelectItem value="by_attribute">By Attribute</SelectItem>
            <SelectItem value="by_attribute_and_sample">
              By Attribute and Sample
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          How to organize citation images on the sheet
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="startRow">Starting Row</Label>
          <Input
            id="startRow"
            type="number"
            min={1}
            value={formData.startRow ?? 1}
            onChange={(e) =>
              handleChange("startRow", parseInt(e.target.value, 10) || 1)
            }
            placeholder="1"
            disabled={readOnly}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="imagesPerRow">Images Per Row</Label>
          <Input
            id="imagesPerRow"
            type="number"
            min={1}
            value={formData.imagesPerRow ?? 1}
            onChange={(e) =>
              handleChange("imagesPerRow", parseInt(e.target.value, 10) || 1)
            }
            placeholder="1"
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="includeAnnotations"
          checked={formData.includeAnnotations ?? true}
          onCheckedChange={(checked) =>
            handleChange("includeAnnotations", checked === true)
          }
          disabled={readOnly}
        />
        <Label htmlFor="includeAnnotations" className="cursor-pointer">
          Include inline bounding box annotations
        </Label>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="includeNonAttributeTests"
          checked={formData.includeNonAttributeTests ?? false}
          onCheckedChange={(checked) =>
            handleChange("includeNonAttributeTests", checked === true)
          }
          disabled={readOnly}
        />
        <Label htmlFor="includeNonAttributeTests" className="cursor-pointer">
          Include non-attribute tests
        </Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="About include non-attribute tests"
                className="inline-flex"
              >
                <Info
                  aria-hidden="true"
                  className="h-4 w-4 text-muted-foreground"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">
                When enabled, adds a single &quot;Non-Attribute Tests&quot; tab
                carrying evidence for test columns that are not linked to a
                control attribute. When disabled, those citations are left out
                of the export.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="rounded-md bg-purple-50 border border-purple-200 p-3 text-sm">
        <strong>Preview:</strong> {formData.sheetNamePrefix}_[ProcedureName] -
        One sheet per procedure
        <div className="mt-2 text-xs">
          Organization: {currentOrganization} | Starting at row{" "}
          {formData.startRow ?? 1}
        </div>
        <div className="mt-1 text-xs">
          {formData.imagesPerRow ?? 1} image(s) per row
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Generates citation image asset sheets for each procedure with
          supporting evidence
        </div>
      </div>

      <Collapsible>
        <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm font-medium hover:bg-stone-100 [&[data-state=open]>svg]:rotate-180">
          Advanced Settings
          <ChevronDown className="h-4 w-4 transition-transform duration-200" />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="truncateEvalColumnStepsTaken"
              checked={formData.truncateEvalColumnStepsTaken ?? false}
              onCheckedChange={(checked) =>
                handleChange("truncateEvalColumnStepsTaken", checked === true)
              }
              disabled={readOnly}
            />
            <Label
              htmlFor="truncateEvalColumnStepsTaken"
              className="cursor-pointer"
            >
              Truncate eval column at &apos;steps taken&apos;
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    When enabled, citation sheet evaluation columns will only
                    show the main conclusion, removing detailed "steps taken"
                    section
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sheetNameTemplate">Sheet Name Template</Label>
            <Input
              id="sheetNameTemplate"
              value={formData.sheetNameTemplate ?? ""}
              onChange={(e) =>
                handleChange("sheetNameTemplate", e.target.value || undefined)
              }
              placeholder={defaultTemplate}
              disabled={readOnly}
            />
            <p className="text-sm text-muted-foreground">
              Customize the generated sheet names using template variables.
              Leave empty to use the default template.
            </p>
          </div>

          <div className="rounded-md bg-stone-50 border border-stone-200 p-3">
            <p className="text-sm font-medium mb-2">Available Variables</p>
            <div className="space-y-1">
              {availableVariables.map((variable) => (
                <div key={variable.name} className="text-sm">
                  <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs font-mono">
                    {variable.name}
                  </code>
                  <span className="text-muted-foreground ml-2">
                    {variable.description}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm">
            <p className="font-medium mb-1">Default Template</p>
            <code className="bg-blue-100 px-2 py-1 rounded text-xs font-mono">
              {defaultTemplate}
            </code>
            <p className="mt-2 text-xs text-muted-foreground">
              The default template changes based on the selected organization
              mode.
            </p>
          </div>

          <div className="rounded-md bg-purple-50 border border-purple-200 p-3 text-sm">
            <strong>Example Output:</strong>{" "}
            {effectiveTemplate
              .replace("{prefix}", formData.sheetNamePrefix || "Citations")
              .replace("{procedure_index}", "1")
              .replace("{procedure_name}", "Invoice Review")
              .replace("{sample}", "S1")
              .replace("{column}", "Amount")
              .replace("{attribute}", "Completeness")}
            <div className="mt-2 text-xs text-muted-foreground">
              Based on your current template:{" "}
              <code className="bg-purple-100 px-1 rounded">
                {effectiveTemplate}
              </code>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
